







































































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { TimeRange } from "@/data/models/TimeRange";
import router from "@/router/router";
import {
  ProgramChain,
  getHeatRatioOfMostRecentRun,
  createNewDryRunProgramChain,
} from "@/data/models/ProgramChain";
import { isIdleRun } from "@/data/models/RunOverview";

import IresDeviceStatusComponent from "@/components/devices/IresDeviceStatus.vue";
import DryingProgressGraphWrapperComponent from "@/components/panel/DryingProgressGraphWrapper.vue";

import * as _ from "lodash";
import { COLOR_LAST_PROJECT_DEVICE_ASSIGNMENT_LINK, COLOR_PROJECT_ASSIGNMENT_LINK, COLOR_PROJECT_DEVICE_ASSIGNMENT_LINK_ACTION } from "@/utils/colors";
import { themedFill } from "@/utils/Theme";
import {
  getIresDeviceCategory,
  getIresDeviceStatus,
  IresDevice,
  IresDeviceCategory,
  isActiveProgramChainOfIresDevice,
  isInStandBy,
  isInWarningOrError,
  isPoweredOff,
} from "@/data/models/IresDevice";
import {
  ComBox,
  ComBoxDeviceState,
  getComBoxState,
  getCurrentSensorData,
} from "@/data/models/ComBox";
import Panel, { isEdgePanel } from "@/data/models/Panel";
import { formatTemperatuers, joinToString } from "@/utils/LocalisationHelper";
import { isGuideSensorSet } from "@/data/models/SampleInfo";
import {
  ComBoxSensorData,
  formatHumidity,
  formatTemperature,
} from "@/data/models/ComBoxSensorData";

import {
  LatestIresDeviceInfo,
  isCurrentAssignmentMissingProgramChain,
} from "@/data/models/LatestIresDeviceInfo";
import { 
  getBranchesOfClient, 
  getBranchesOfMemberClient, 
  getMemberClientIds, 
  getProjectManagementType, 
  shouldHideSerialNumber 
} from "@/data/repository/ClientRepository";
import Client, { ProjectManagement } from "@/data/models/Client";
import { Branch, getBranchName } from "@/data/models/Branch";
import { viewHyDryDeviceDetailsOfMemberAccess, aiPredictionsAccess, showAdditionalDeviceInfoAccess } from "@/permissions/UserPermissions";
import { manageProjectsClientAccess } from "@/permissions/ClientPermissions";
import { getAiPredictionsAccess } from "@/data/repository/ClientRepository";
import { setLastVisitedItem, StorageKeyLastVisitedItemLevel } from "@/utils/Utils";
import {
  getDailyPredictionByExecutionId,
  observeAiPredictionLatest,
} from "@/data/repository/AiPredictionRepository";
import {
  AiPredictionLatest,
  getQualityScoreColor,
  getQualityScoreBorderStyle,
  getTrafficLightColor,
  getTrafficLightBorderStyle,
} from "@/data/models/AiPrediction";
import { getEffectiveUser } from "@/utils/ViewAs";

export interface ProjectInfo {
  currentProjectAssignmentTimeRange?: TimeRange;
  projectName?: string;
  projectNumber?: string;
  projectId?: string;
}

@Component({
  components: {
    "ires-device-status": IresDeviceStatusComponent,
    "drying-progress": DryingProgressGraphWrapperComponent,
  },
})
export default class IresDeviceOverviewItemComponent extends Vue {
  @Prop() iresDevice!: IresDevice;
  @Prop() elevation!: number;
  @Prop({ default: true }) showProjectInfo!: boolean;

  @Prop({ default: null }) projectInfo!: ProjectInfo | null;
  @Prop({ default: false }) isInfoOfLastProject!: boolean;
  @Prop({ default: [] }) programChains!: Array<ProgramChain>;

  @Prop({ default: true }) showIresDeviceStatus!: boolean;
  @Prop({ default: "" }) runningSince!: string;
  @Prop({ default: true }) showDryingGraph!: boolean;

  @Prop() lastRefreshDate!: Date;
  @Prop({ default: true }) componentVisible!: boolean;

  @Prop({ default: true }) showLastUpdateText!: boolean;
  @Prop() inventoryNumber!: string;

  @Prop({ default: false }) isSelectionMode!: boolean;
  @Prop({ default: false }) isSelected!: boolean;
  @Prop({ default: false }) disableSelection!: boolean;
  @Prop({ default: "" }) iresDeviceComments!: string;
  @Prop({ default: "" }) backgroundStyle!: string;

  @Prop({ default: () => new Map<string, Client>() })
  memberClients!: Map<string, Client>;

  @Prop({ default: false })
  isAllowedToMoveDevicesToAnotherClient!: boolean;

  aiPredictionLatest: AiPredictionLatest | null = null;
  aiPredictionUnsubscribe: (() => void) | null = null;
  aiPredictionResolvedQualityScore: number | null = null;
  private aiPredictionQualityScoreRequestKey = "";

  get isDeviceSelected() {
    return this.isSelected;
  }

  set isDeviceSelected(value: boolean) {
    if (value !== this.isSelected && !this.disableSelection) {
      this.$emit("selectIresDevice");
    }
  }

  /**
   * Returns the border style for the mini-verlauf drying progress card
    * based on the AI quality score gradient. Falls back to traffic light for
    * older data without qualityScore. Only visible to IRES admins
   * when the AI predictions toggle is enabled.
   */
  get aiTrafficLightBorderStyle(): string {
    if (!this.shouldShowAiTrafficLight) {
      return "";
    }

    if (!this.aiPredictionLatest) {
      return "";
    }

    const qualityBorderStyle = getQualityScoreBorderStyle(this.effectiveQualityScore);
    if (qualityBorderStyle) {
      return qualityBorderStyle;
    }

    return getTrafficLightBorderStyle(this.aiPredictionLatest.trafficLight);
  }

  /**
   * Resolved color currently used for the mini drying graph border.
   * Prefers quality-score gradient and falls back to traffic-light color.
   */
  get aiTrafficLightBorderColor(): string {
    if (!this.shouldShowAiTrafficLight || !this.aiPredictionLatest) {
      return "";
    }

    if (this.effectiveQualityScore != null) {
      return getQualityScoreColor(this.effectiveQualityScore);
    }

    return getTrafficLightColor(this.aiPredictionLatest.trafficLight);
  }

  /**
   * Effective quality score used for border rendering.
   * Prefers score on `aiPrediction.latest`, then falls back to resolved score
   * loaded from the latest daily prediction document.
   */
  get effectiveQualityScore(): number | null {
    if (!this.aiPredictionLatest) {
      return null;
    }

    if (this.aiPredictionLatest.qualityScore != null) {
      return this.aiPredictionLatest.qualityScore;
    }

    return this.aiPredictionResolvedQualityScore;
  }

  /**
   * Whether the AI traffic light border should be shown.
   * Only visible for ComBox devices (not panels) when enableAiPredictions is ON
   * and user has VIEW_AI_PREDICTIONS capability (currently in IRES_CAPABILITIES,
   * move to general capabilities for client rollout).
   */
  get shouldShowAiTrafficLight(): boolean {
    if (getIresDeviceCategory(this.iresDevice) !== IresDeviceCategory.COMBOX) {
      return false;
    }
    const user = getEffectiveUser();
    return aiPredictionsAccess(user) && (getAiPredictionsAccess() ?? false);
  }

  /** The user whose view this shows: the chosen one while "Sicht auf" is active (ticket 2610052). */
  get activeUser() {
    return getEffectiveUser();
  }

  get isAllowedToAccessProjects() {
    return manageProjectsClientAccess(this.activeUser);
  }

  get cursorStyleForProjectItem() {
    return this.isAllowedToAccessProjects
      ? "pointer"
      : "default";
  }

  get shouldHideSerialNumber() {
    return shouldHideSerialNumber();
  }

  get iresDeviceDescriptionText() {
    return !this.shouldHideSerialNumber && this.inventoryNumber !== this.iresDevice.serialNo
      ? this.inventoryNumber + " / " + this.iresDevice.serialNo
      : this.inventoryNumber;
  }

  get clientInformation() {
    if (this.isAllowedToViewIresDeviceDetailsOfMemberClients && this.assignedClientId) {
      const client = this.memberClients.get(this.assignedClientId)

      if (!client) {
        return null;
      }

      const clientName =
        !client.companyName || client.companyName === ""
          ? client.clientName
          : client.companyName;
      
          return clientName;
    }
    else {
      return null;
    }
  }

  get clientBranches() {
    if (this.isAllowedToViewIresDeviceDetailsOfMemberClients && this.assignedClientId) {
      const client = this.memberClients.get(this.assignedClientId)

      if (client) {
        return getBranchesOfMemberClient(client);
      }
    }
    
    return [];
  }

  get branchInformation() {
    if (!this.isAllowedToMoveDevicesToAnotherClient) {
      const branch = getBranchesOfClient().find(
        (branch: Branch) => branch.branchNumber.toString() === this.iresDevice.currentAssignment?.deviceBranch?.toString()
      ) as Branch;

      if (!branch) {
        return null;
      }

      return this.$t("common.branch").toString() + ": " + getBranchName(branch);
    }

    if (this.isAllowedToViewIresDeviceDetailsOfMemberClients && this.assignedClientId) {
      const client = this.memberClients.get(this.assignedClientId)

      if (!client) {
        return null;
      }

      const branch = this.clientBranches.find(
        (branch: Branch) => branch.branchNumber.toString() === this.iresDevice.currentAssignment?.deviceBranch?.toString()
      ) as Branch;

      if (!branch) {
        return null;
      }

      return this.$t("common.branch").toString() + ": " + getBranchName(branch);
    }
    else {
      return null;
    }
  }

  get assignedClientId() {
    return this.iresDevice?.currentAssignment?.clientId;
  }

  get isAllowedToViewIresDeviceDetailsOfMemberClients() {
    const activeUser = getEffectiveUser();

    return viewHyDryDeviceDetailsOfMemberAccess(activeUser) && this.isMemberClient;
  }

  get isMemberClient() {
    return this.assignedClientId ? this.memberClientIds.includes(this.assignedClientId) : false;
  }

  get memberClientIds() {
    return getMemberClientIds();
  }

  get firstLine() {
    const projectInfo = this.projectInfo;

    if (!projectInfo) {
      return "";
    }

    if (projectInfo.projectName && projectInfo.projectName !== "") {
      return projectInfo.projectName;
    }
    else if (projectInfo.projectNumber && projectInfo.projectNumber !== "") {
      return projectInfo.projectNumber;
    }

    return "";
  }

  get showAssignmentWarning() {
    if (!this.isAssignedToProject) {
      return false;
    }

    const latestIresDeviceInfo: LatestIresDeviceInfo = {
      iresDevice: this.iresDevice,
      mostRecentProgramChain: this.mostRecentProgramChain,
    };

    return isCurrentAssignmentMissingProgramChain(latestIresDeviceInfo);
  }

  /** The link card's fill; dark mode takes the palette shade of its colour. */
  get projectButtonColor() {
    if (this.isInfoOfLastProject) {
      return themedFill(COLOR_LAST_PROJECT_DEVICE_ASSIGNMENT_LINK);
    }
    
    return themedFill(this.showAssignmentWarning 
      ? COLOR_PROJECT_DEVICE_ASSIGNMENT_LINK_ACTION 
      : COLOR_PROJECT_ASSIGNMENT_LINK);
  }

  formatComBoxSensorData(
    formatI18n: string,
    comBoxSensorData: ComBoxSensorData
  ) {
    if (
      comBoxSensorData.temperature === undefined ||
      comBoxSensorData.relativeHumidity === undefined
    ) {
      return null;
    }

    const temperature = formatTemperature(this, comBoxSensorData.temperature);
    const humidity = formatHumidity(this, comBoxSensorData);

    return this.$t(formatI18n, [temperature, humidity]).toString();
  }

  get sensorInfo() {
    if (isPoweredOff(this.iresDevice) || isInStandBy(this.iresDevice)) {
      return "";
    }

    const iresDeviceCategory = getIresDeviceCategory(this.iresDevice);

    if (iresDeviceCategory === IresDeviceCategory.PANEL) {
      const panel = this.iresDevice as Panel;
      const panelStatus = panel.panelStatus;

      if (!panelStatus || isPoweredOff(panel)) {
        return "";
      }

      const temperature = formatTemperatuers(
        this,
        isEdgePanel(panel),
        panelStatus.currentWallTemp,
        panelStatus.currentWallTemp2
      );

      const heatRatio = this.panelHeatRatio;

      return joinToString([temperature, heatRatio], "\n");
    } else {
      const comBox = this.iresDevice as ComBox;
      const comBoxStatus = comBox.comBoxStatus;

      const currentSensorData = getCurrentSensorData(comBoxStatus);
      const guideSensor = comBoxStatus.currentGuideSensor;

      const isGuideSensorValid = isGuideSensorSet(guideSensor);
      const floorSensorInfo = isGuideSensorValid
        ? this.formatComBoxSensorData(
            "comBox.deviceList.formatSensorValuesFloor",
            currentSensorData[guideSensor]
          )
        : null;

      const roomSensorInfo = this.formatComBoxSensorData(
        "comBox.deviceList.formatSensorValuesRoom",
        currentSensorData[4]
      );

      return joinToString([roomSensorInfo, floorSensorInfo], "\n");
    }
  }

  get programChainToDisplay() {
    return this.selectedProgramChain;
  }

  get dryingGraphEndDate() {
    const programChain = this.selectedProgramChain;
    const iresDevice = this.iresDevice;

    if (!programChain || !iresDevice) {
      return null;
    }

    return isActiveProgramChainOfIresDevice(iresDevice, programChain)
      ? new Date()
      : null;
  }

  get smCols() {
    return this.showProjectInfo ? 4 : 5;
  }

  get dryingCols() {
    return this.showProjectInfo ? 2 : 4;
  }

  get isAssignedToProject(): boolean {
    return this.projectInfo !== null;
  }

  /**
   * The last-event line under a device in warning or error. IRES-only, decided in the "HyDry device
   * events: client visibility review": it shows the raw device event, which is exactly the
   * device-internal detail SHOW_ADDITIONAL_DEVICE_INFO governs everywhere else.
   */
  get showLastEvent() {
    return (
      this.isAllowedToViewAdditionalInfo &&
      isInWarningOrError(this.iresDevice) &&
      this.lastEvent
    );
  }

  get isAllowedToViewAdditionalInfo() {
    return showAdditionalDeviceInfoAccess(this.activeUser);
  }

  get deviceStatus() {
    return getIresDeviceStatus(this.iresDevice);
  }

  /**
   * The raw device event number. It used to read `lastClientEvent`, which only a Panel ever gets, so
   * a ComBox in warning showed no line at all.
   */
  get lastEventNumber() {
    return this.deviceStatus?.lastEvent;
  }

  /** The event vocabularies are per device type and share numbers with different meanings. */
  get lastEventResourcePrefix() {
    return getIresDeviceCategory(this.iresDevice) === IresDeviceCategory.PANEL
      ? "panel.events.event"
      : "comBox.events.event";
  }

  get lastEvent() {
    const lastEventNumber = this.lastEventNumber;

    if (!lastEventNumber) {
      return null;
    }

    return this.$t(this.lastEventResourcePrefix, [lastEventNumber]).toString();
  }

  get lastEventDescription() {
    const lastEventNumber = this.lastEventNumber;

    if (!lastEventNumber) {
      return "";
    }

    const resourceId = `${this.lastEventResourcePrefix}${lastEventNumber}`;
    const resolved = this.$t(resourceId);

    if (resolved === resourceId) {
      return null;
    } else {
      return resolved;
    }
  }

  get mostRecentProgramChain() {
    return this.programChains[0] ?? null;
  }

  get selectedProgramChain(): ProgramChain | null {
    if (!this.iresDevice) {
      return null;
    }

    const programChain = this.mostRecentProgramChain;

    if (!programChain) {
      return null;
    }

    const iresDeviceCategory = getIresDeviceCategory(this.iresDevice);

    if (iresDeviceCategory === IresDeviceCategory.COMBOX) {
      const comBox = this.iresDevice as ComBox;

      if (getComBoxState(comBox) == ComBoxDeviceState.IDLE) {
        return createNewDryRunProgramChain(comBox.serialNo);
      }
    }

    return programChain;
  }

  get panelHeatRatio() {
    if (isPoweredOff(this.iresDevice)) {
      return null;
    }

    const programChain = this.selectedProgramChain;

    if (!programChain) {
      return null;
    }

    const containsActiveRun = _.some(
      programChain.runs,
      (run) => !isIdleRun(run)
    );

    if (!containsActiveRun) {
      return null;
    }

    const heatRatio = getHeatRatioOfMostRecentRun(programChain);

    if (!heatRatio) {
      return null;
    }

    return this.$t("panelDetail.formatHeatRatioWithLabel", [
      heatRatio,
    ]).toString();
  }

  onCardClick(event: MouseEvent) {
    const targetElement = event.target as HTMLElement | null;

    if (this.isSelectionMode && targetElement?.closest(".v-input--selection-controls")) {
      return;
    }

    this.openIresDeviceDetails();
  }

  openIresDeviceDetails() {
    this.$emit("openIresDeviceDetails");
  }

  mounted() {
    this.startAiPredictionObservation();
  }

  beforeDestroy() {
    this.stopAiPredictionObservation();
  }

  /**
   * Restarts the AI prediction Firestore observation when programChains prop
   * is populated asynchronously (loaded via visibilityChanged in parent).
   */
  @Watch("mostRecentProgramChain")
  onMostRecentProgramChainChanged() {
    this.stopAiPredictionObservation();
    this.startAiPredictionObservation();
  }

  /**
   * Starts a real-time Firestore listener for the AI prediction summary
   * on the most recent program chain. Only subscribes if the user is
   * an IRES admin and AI predictions are enabled.
   */
  private startAiPredictionObservation() {
    if (!this.shouldShowAiTrafficLight) {
      return;
    }

    const programChain = this.mostRecentProgramChain;
    if (!programChain || !this.iresDevice) {
      return;
    }

    this.aiPredictionUnsubscribe = observeAiPredictionLatest(
      this.iresDevice.serialNo,
      programChain.id,
      async (latest) => {
        this.aiPredictionLatest = latest;

        await this.resolveFallbackQualityScore(latest, programChain.id);
      }
    );
  }

  /**
   * Stops the Firestore listener for AI prediction data.
   */
  private stopAiPredictionObservation() {
    if (this.aiPredictionUnsubscribe) {
      this.aiPredictionUnsubscribe();
      this.aiPredictionUnsubscribe = null;
    }
    this.aiPredictionResolvedQualityScore = null;
    this.aiPredictionQualityScoreRequestKey = "";
  }

  /**
   * For older chain summaries where `aiPrediction.latest.qualityScore` is not
   * populated yet, load the corresponding daily prediction doc and derive score
   * from there (prefer document-level score, fallback to `all` window score).
   */
  private async resolveFallbackQualityScore(
    latest: AiPredictionLatest | null,
    chainId: string
  ) {
    this.aiPredictionResolvedQualityScore = null;

    if (!latest || latest.qualityScore != null || !latest.latestExecutionId) {
      this.aiPredictionQualityScoreRequestKey = "";
      return;
    }

    if (latest.source !== "daily") {
      this.aiPredictionQualityScoreRequestKey = "";
      return;
    }

    const requestKey = `${chainId}:${latest.latestExecutionId}`;
    this.aiPredictionQualityScoreRequestKey = requestKey;

    const dailyPrediction = await getDailyPredictionByExecutionId(
      this.iresDevice.serialNo,
      chainId,
      latest.latestExecutionId
    );

    if (this.aiPredictionQualityScoreRequestKey !== requestKey) {
      return;
    }

    let qualityScore = dailyPrediction?.qualityScore;

    if (qualityScore == null) {
      const windows = dailyPrediction?.results?.windows;
      if (Array.isArray(windows) && windows.length > 0) {
        const allWindow = windows.find((windowResult) => String(windowResult.window) === "all") ?? windows[0];
        qualityScore = allWindow?.qualityScore;
      }
    }

    this.aiPredictionResolvedQualityScore =
      typeof qualityScore === "number" ? qualityScore : null;
  }

  openAssignedProject() {
    if (!this.isAllowedToAccessProjects) {
      return;
    }
    
    const projectId = this.projectInfo?.projectId;

    if (getProjectManagementType() === ProjectManagement.DEVICES_ONLY_WEBSITE) {
      return;
    }

    if (!projectId) {
      return;
    }

    if (projectId) {
      router.push({
        path: `/main/projects/${projectId}`,
      });
    }
  }

  openDryingDetails() {
    if (
      !this.selectedProgramChain || 
      this.selectedProgramChain.runs.length === 0
    ) {
      return;
    }

    setLastVisitedItem(
      this.iresDevice.serialNo,
      StorageKeyLastVisitedItemLevel.LEVEL_1,
    );

    const isActive = this.programChainToDisplay
      ? isActiveProgramChainOfIresDevice(
          this.iresDevice,
          this.programChainToDisplay
        )
      : false;

    const chainId = isActive
      ? undefined
      : this.selectedProgramChain.id;

    if (chainId || isActive) {
      router.push({
        path: `/main/devices/deviceDetail`,
        query: {
          serialNumber: this.iresDevice.serialNo,
          chainId,
        },
      });
    }
  }

}
